import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Briefcase } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ErrorState, LoadingState, PageEmptyState } from '../../../components/layout';
import { useAuth } from '../../../contexts/authContext';
import { useOpportunities } from '../hooks/useOpportunities';
import { useOpportunity } from '../hooks/useOpportunity';
import { OpportunityCard } from './OpportunityCard';
import { OpportunityDetailModal } from './OpportunityDetailModal';
/** RFC3339 timestamp at the END of the day `days` from now — morgans parses
 * deadline/posted filters with time.Parse(RFC3339), so a bare date string
 * would be rejected. Pinned to end-of-day (not "now + N") so the value is
 * STABLE across renders within the same day: a millisecond-precise "now"
 * would change every render, churn the React Query key, and refetch in a
 * loop. Day granularity is all a "due within N days" filter needs. */ function toRfc3339FromDaysFromNow(days) {
    const d = new Date();
    d.setDate(d.getDate() + days);
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
}
/** RFC3339 timestamp at the START of today — the lower bound for the urgency
 * chips, so already-expired postings are excluded from "Due in N days"
 * results. Pinned to start-of-day for the same render-stability reason as
 * toRfc3339FromDaysFromNow. */ function startOfTodayRfc3339() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
}
/** Convert a `<input type="date">` value (YYYY-MM-DD) to an RFC3339 timestamp.
 * Returns undefined for empty input. Without this, dialog date filters send a
 * bare YYYY-MM-DD that fails morgans' RFC3339 parse and errors the whole list.
 *
 * The date is interpreted in LOCAL time (matching the urgency chips, which use
 * local day boundaries — `new Date('YYYY-MM-DD')` would parse as UTC midnight
 * and drift). `boundary` picks which edge of the selected day to use so the
 * chosen day is inclusive: 'start' for lower bounds (postedAfter), 'end' for
 * upper bounds (deadlineBefore) — otherwise "Due Before Jun 1" would exclude
 * all of Jun 1. */ function toRfc3339FromDateInput(value, boundary) {
    if (!value) return undefined;
    const [y, m, day] = value.split('-').map(Number);
    if (!y || !m || !day) return undefined;
    const d = boundary === 'end' ? new Date(y, m - 1, day, 23, 59, 59, 999) : new Date(y, m - 1, day, 0, 0, 0, 0);
    return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}
export function OpportunityList({ search, filters, quickFilters, viewMode, onTotalChange, onFilteredCountChange }) {
    const [currentPage, setCurrentPage] = useState(0);
    const [selectedOpportunity, setSelectedOpportunity] = useState(null);
    const [navigatingToNoticeId, setNavigatingToNoticeId] = useState(null);
    const { isLoading: authIsLoading } = useAuth();
    const { data: navigatedOpportunity } = useOpportunity(navigatingToNoticeId);
    // When the fetched opportunity arrives, swap the drawer to it
    useEffect(()=>{
        if (navigatedOpportunity) {
            setSelectedOpportunity(navigatedOpportunity);
            setNavigatingToNoticeId(null);
        }
    }, [
        navigatedOpportunity
    ]);
    useEffect(()=>{
        setCurrentPage(0);
    }, [
        search,
        filters,
        quickFilters
    ]);
    const limit = 20;
    const offset = currentPage * limit;
    // Urgency chips map to a server-side deadline WINDOW so the filter spans the
    // whole dataset, not just the loaded page. "Due in 7 days" is a subset of
    // "Due in 30 days". The lower bound (deadlineAfter = start of today) is what
    // walls off already-expired postings — active_only is a separate boolean
    // column on the row, NOT a deadline check, so it does not exclude a posting
    // whose deadline has passed. Only the chips set deadlineAfter; the advanced
    // dialog's "Due Before" stays unbounded so expired postings remain viewable
    // there.
    const urgencyActive = quickFilters.urgency !== 'all';
    const urgencyDeadlineBefore = urgencyActive ? toRfc3339FromDaysFromNow(quickFilters.urgency === 'urgent' ? 7 : 30) : undefined;
    const deadlineAfter = urgencyActive ? startOfTodayRfc3339() : undefined;
    // The dialog's deadlineBefore is a bare YYYY-MM-DD from <input type="date">;
    // morgans parses RFC3339, so normalize it (else the whole request errors).
    // The urgency chip takes precedence when both are set.
    const deadlineBefore = urgencyDeadlineBefore ?? toRfc3339FromDateInput(filters.deadlineBefore, 'end');
    // Set-aside has two UIs (quick chip + dialog) that write the same param —
    // resolve the collision explicitly: the chip wins when set.
    const setAsideType = quickFilters.setAside ?? filters.setAsideType;
    // Convert all filters to API params — everything is server-side.
    const apiParams = {
        limit,
        offset,
        search: search || undefined,
        agency: filters.agency,
        naicsCode: filters.naicsCode,
        postedAfter: toRfc3339FromDateInput(filters.postedAfter, 'start'),
        ...setAsideType && {
            setAsideType
        },
        ...deadlineAfter && {
            deadlineAfter
        },
        ...deadlineBefore && {
            deadlineBefore
        },
        ...quickFilters.source && {
            source: quickFilters.source
        },
        ...quickFilters.saved && {
            saved: true
        }
    };
    const { data, isLoading, error, refetch } = useOpportunities(apiParams, !authIsLoading);
    const opportunities = data?.opportunities || [];
    const hasMore = data?.hasMore || false;
    const total = data?.total || 0;
    // All filters are server-side now, so `total` already reflects them — there
    // is no separate client-filtered subset to report.
    useEffect(()=>{
        onTotalChange(total);
        onFilteredCountChange(undefined);
    }, [
        total,
        onTotalChange,
        onFilteredCountChange
    ]);
    const handleNextPage = ()=>{
        if (hasMore) {
            setCurrentPage((prev)=>prev + 1);
        }
    };
    const handlePrevPage = ()=>{
        if (currentPage > 0) {
            setCurrentPage((prev)=>prev - 1);
        }
    };
    if (authIsLoading || isLoading) {
        return /*#__PURE__*/ _jsx(LoadingState, {
            rows: 4
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsx(ErrorState, {
            message: "There was an error loading opportunities. Please try again.",
            onRetry: refetch
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            search && /*#__PURE__*/ _jsxs("div", {
                className: "mb-4 text-sm text-muted-foreground",
                children: [
                    "Found ",
                    total.toLocaleString(),
                    ' results for "',
                    search,
                    '"'
                ]
            }),
            opportunities.length === 0 ? /*#__PURE__*/ _jsx(PageEmptyState, {
                icon: /*#__PURE__*/ _jsx(Briefcase, {}),
                title: "No opportunities found",
                description: search ? `No results match "${search}". Try different search terms.` : 'No opportunities available at this time.',
                size: "sm",
                dataTestid: Testids.opportunities.emptyState
            }) : /*#__PURE__*/ _jsx("div", {
                className: viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4' : 'space-y-4',
                "data-testid": "opportunities-list",
                "data-view-mode": viewMode,
                children: opportunities.map((opportunity)=>/*#__PURE__*/ _jsx(OpportunityCard, {
                        opportunity: opportunity,
                        onClick: ()=>setSelectedOpportunity(opportunity)
                    }, opportunity._id))
            }),
            opportunities.length > 0 && /*#__PURE__*/ _jsxs("div", {
                className: "mt-6 flex items-center justify-between",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "text-sm text-muted-foreground",
                        children: [
                            "Showing ",
                            offset + 1,
                            " - ",
                            Math.min(offset + limit, total),
                            " of ",
                            total.toLocaleString()
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                variant: "outline",
                                onClick: handlePrevPage,
                                disabled: currentPage === 0,
                                "data-testid": "pagination-prev",
                                children: "Previous"
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                variant: "outline",
                                onClick: handleNextPage,
                                disabled: !hasMore,
                                "data-testid": "pagination-next",
                                children: "Next"
                            })
                        ]
                    })
                ]
            }),
            selectedOpportunity && /*#__PURE__*/ _jsx(OpportunityDetailModal, {
                opportunity: selectedOpportunity,
                open: !!selectedOpportunity,
                onOpenChange: (open)=>{
                    if (!open) setSelectedOpportunity(null);
                },
                onNavigate: (noticeId)=>setNavigatingToNoticeId(noticeId)
            })
        ]
    });
}
